import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UserDetailModal from "./UserDetailModal";
import { useGetCopilotUsersQuery, useGetEnterpriseCopilotUsersQuery } from "state/rtkQuery/copilot/copilotQueries";
import { useDebounce } from "utils/useDebounce";

const ACCENT = "#26890d";
const USAGE_RED = "#E2231A";
const USAGE_ORANGE = "#F28C00";
const USAGE_YELLOW = "#FFCD00";
const ROWS_PER_PAGE = 10;
const MAIN_VISIBLE_ROWS = 10;

const getBudgetColor = (pct) => {
  if (pct >= 80) return USAGE_RED;
  if (pct >= 50) return USAGE_ORANGE;
  return USAGE_YELLOW;
};

const BudgetBar = ({ budget }) => {
  if (!budget) {
    return (
      <Typography sx={{ fontSize: "0.65rem", color: "#bbb", fontStyle: "italic", mt: 0.5 }}>
        No budget set
      </Typography>
    );
  }

  const { budget_amount = 0, consumed_amount = 0 } = budget;
  const pct = budget_amount > 0 ? Math.min((consumed_amount / budget_amount) * 100, 100) : 0;
  const barColor = getBudgetColor(pct);

  return (
    <Box sx={{ mt: 0.6 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.3 }}>
        <Typography sx={{ fontSize: "10px", color: "#3a3a3a" }}>
          ${consumed_amount.toFixed(2)} / ${budget_amount.toFixed(2)} (Current Budget)
        </Typography>
        <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: barColor }}>
          {pct.toFixed(0)}%
        </Typography>
      </Box>
      <Tooltip
        title={`$${consumed_amount.toFixed(2)} of $${budget_amount.toFixed(2)} budget consumed`}
        placement="right"
        arrow
      >
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 4,
            borderRadius: 4,
            backgroundColor: "#f0f0f0",
            "& .MuiLinearProgress-bar": { backgroundColor: barColor, borderRadius: 4 },
          }}
        />
      </Tooltip>
    </Box>
  );
};

const headerCellSx = {
  fontWeight: 700,
  fontSize: "0.78rem",
  color: "#555",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  backgroundColor: "#fafafa",
  borderBottom: "2px solid #e8e8e8",
  py: 1.5,
};

const bodyCellSx = {
  fontSize: "0.85rem",
  color: "#1a1a1a",
  py: 1.2,
  borderBottom: "1px solid #f0f0f0",
};

const formatAIC = (v) => v.toLocaleString("en-AU", { maximumFractionDigits: 2 });
const formatSpend = (v) =>
  "$" + v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const EngineerTable = ({ orgId, costCenterId, platformId, fromDate, toDate, deloitteOverageRate, isEnterprise = false }) => {
  const [open, setOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalPage, setModalPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("total_consumed_credits"); // API sort_by values
  const [sortOrder, setSortOrder] = useState("desc");

  const debouncedSearch = useDebounce(searchQuery, 300);

  const skip = isEnterprise ? !platformId : !(orgId || costCenterId) || !platformId;

  // Top panel: always page 1, sorted by consumption, fixed count
  const scopedTopQuery = useGetCopilotUsersQuery(
    { orgId, costCenterId, platformId, fromDate, toDate, sortBy: "total_consumed_credits", sortOrder: "desc", page: 1, perPage: MAIN_VISIBLE_ROWS },
    { skip: skip || isEnterprise, refetchOnMountOrArgChange: true }
  );
  const enterpriseTopQuery = useGetEnterpriseCopilotUsersQuery(
    { platformId, fromDate, toDate, sortBy: "total_consumed_credits", sortOrder: "desc", page: 1, perPage: MAIN_VISIBLE_ROWS },
    { skip: skip || !isEnterprise, refetchOnMountOrArgChange: true }
  );
  const { data: topData, isLoading: topLoading } = isEnterprise ? enterpriseTopQuery : scopedTopQuery;

  // Modal: server-side sort + pagination + search
  const scopedModalQuery = useGetCopilotUsersQuery(
    { orgId, costCenterId, platformId, fromDate, toDate, search: debouncedSearch, sortBy, sortOrder, page: modalPage, perPage: ROWS_PER_PAGE },
    { skip: skip || isEnterprise || !open, refetchOnMountOrArgChange: true }
  );
  const enterpriseModalQuery = useGetEnterpriseCopilotUsersQuery(
    { platformId, fromDate, toDate, search: debouncedSearch, sortBy, sortOrder, page: modalPage, perPage: ROWS_PER_PAGE },
    { skip: skip || !isEnterprise || !open, refetchOnMountOrArgChange: true }
  );
  const { data: modalData, isLoading: isModalLoading, isFetching: isModalFetching } = isEnterprise ? enterpriseModalQuery : scopedModalQuery;

  const topEngineers = topData?.data ?? [];
  const modalRows = modalData?.data ?? [];
  const totalModalCount = modalData?.total ?? 0;
  const totalModalPages = Math.max(1, Math.ceil(totalModalCount / ROWS_PER_PAGE));

  const handleOpen = () => {
    setModalPage(1);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery("");
    setModalPage(1);
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setModalPage(1);
  };

  const TableRows = ({ rows, startIndex = 0, onRowClick }) =>
    rows.map((row, idx) => (
      <TableRow
        key={row.email}
        onClick={() => onRowClick && onRowClick(row)}
        sx={{
          "&:hover": { backgroundColor: "#f9fdf2", cursor: "pointer" },
          "&:last-child td": { border: 0 },
        }}
      >
        <TableCell sx={{ ...bodyCellSx, color: "#999", width: 48, verticalAlign: "top", pt: 1.5 }}>
          {startIndex + idx + 1}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, verticalAlign: "top" }}>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>
            {row.email}
            {row.full_name && (
              <Typography component="span" sx={{ fontSize: "0.78rem", fontWeight: 400, color: "#999" }}>
                {" "}({row.full_name})
              </Typography>
            )}
          </Typography>
          <BudgetBar budget={row.budget} />
        </TableCell>
        {isEnterprise && (
          <TableCell sx={{ ...bodyCellSx, verticalAlign: "top", pt: 1.5 }}>
            {row.org_name && (
              <Typography sx={{ fontSize: "0.78rem", color: "#555" }}>{row.org_name}</Typography>
            )}
            {row.cost_center_name && (
              <Typography sx={{ fontSize: "0.7rem", color: "#999" }}>{row.cost_center_name}</Typography>
            )}
          </TableCell>
        )}
        <TableCell sx={{ ...bodyCellSx, fontWeight: 600, verticalAlign: "top", pt: 1.5 }} align="right">
          {formatAIC(row.total_consumed_credits)}
        </TableCell>
        <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: ACCENT, verticalAlign: "top", pt: 1.5 }} align="right">
          {formatSpend(row.total_amount)}
        </TableCell>
      </TableRow>
    ));

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "#1a1a1a" }}>
              Top Engineers by AIC Usage
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>
              Showing top {MAIN_VISIBLE_ROWS} - sorted by highest consumption
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {fromDate && toDate && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1.2,
                  py: 0.35,
                  backgroundColor: "#f7f7f7",
                  border: "1px solid #e8e8e8",
                  borderRadius: "20px",
                }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#777" }}>
                  {fromDate} → {toDate}
                </Typography>
              </Box>
            )}
          <Tooltip title="View all engineers with full data" placement="left" arrow>
            <IconButton
              onClick={handleOpen}
              size="small"
              sx={{
                color: "#555",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#f2f8e6", borderColor: ACCENT, color: ACCENT },
              }}
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          </Box>
        </Box>

        {/* Table */}
        {topLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200 }}>
            <CircularProgress size={32} sx={{ color: ACCENT }} />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 580, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 48 }}>#</TableCell>
                  <TableCell sx={headerCellSx}>Email</TableCell>
                  {isEnterprise && <TableCell sx={headerCellSx}>Org</TableCell>}
                  <TableCell sx={headerCellSx} align="right">AIC Consumed</TableCell>
                  <TableCell sx={headerCellSx} align="right">Spend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRows
                  rows={topEngineers}
                  startIndex={0}
                  onRowClick={(row) => setSelectedUser(row)}
                />
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Full Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", overflow: "hidden" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `3px solid ${ACCENT}`,
            py: 2,
            px: 3,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
              All Engineers - AIC Consumption &amp; Spend
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#999", mt: 0.3 }}>
              {totalModalCount} engineers {searchQuery && `· matching "${searchQuery}"`}
            </Typography>
          </Box>

          {/* Search & Sort Controls */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <TextField
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setModalPage(1);
              }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: "1.2rem", color: "#999" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  backgroundColor: "#f9f9f9",
                },
              }}
            />

            {/* Sort by Email */}
            <Tooltip title={`Sort by Email ${sortBy === "email" ? (sortOrder === "asc" ? "(A→Z)" : "(Z→A)") : ""}`} placement="top">
              <IconButton
                size="small"
                disabled={isModalLoading || isModalFetching}
                onClick={() => toggleSort("email")}
                sx={{
                  border: sortBy === "email" ? `2px solid ${ACCENT}` : "1px solid #e8e8e8",
                  borderRadius: "6px",
                  backgroundColor: sortBy === "email" ? "#f2f8e6" : "#fff",
                  color: sortBy === "email" ? ACCENT : "#555",
                }}
              >
                {sortBy === "email" && sortOrder === "desc" ? (
                  <ArrowDownwardIcon fontSize="small" />
                ) : sortBy === "email" && sortOrder === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>AZ</Typography>
                )}
              </IconButton>
            </Tooltip>

            {/* Sort by AIC */}
            <Tooltip title={`Sort by AIC ${sortBy === "total_consumed_credits" ? (sortOrder === "asc" ? "(Low→High)" : "(High→Low)") : ""}`} placement="top">
              <IconButton
                size="small"
                disabled={isModalLoading || isModalFetching}
                onClick={() => toggleSort("total_consumed_credits")}
                sx={{
                  border: sortBy === "total_consumed_credits" ? `2px solid ${ACCENT}` : "1px solid #e8e8e8",
                  borderRadius: "6px",
                  backgroundColor: sortBy === "total_consumed_credits" ? "#f2f8e6" : "#fff",
                  color: sortBy === "total_consumed_credits" ? ACCENT : "#555",
                }}
              >
                {sortBy === "total_consumed_credits" && sortOrder === "desc" ? (
                  <ArrowDownwardIcon fontSize="small" />
                ) : sortBy === "total_consumed_credits" && sortOrder === "asc" ? (
                  <ArrowUpwardIcon fontSize="small" />
                ) : (
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>⚡</Typography>
                )}
              </IconButton>
            </Tooltip>

            <IconButton onClick={handleClose} size="small" sx={{ color: "#555" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", height: 560, position: "relative" }}>
          {/* Loading Overlay */}
          {(isModalLoading || isModalFetching) && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.85)",
                zIndex: 10,
                borderRadius: "12px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <CircularProgress sx={{ color: "#26890d" }} size={40} />
                <Typography sx={{ fontSize: "0.8rem", color: "#888" }}>Loading...</Typography>
              </Box>
            </Box>
          )}

          <TableContainer sx={{ flex: 1, overflowY: "auto", opacity: (isModalLoading || isModalFetching) ? 0.5 : 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headerCellSx, width: 52 }}>#</TableCell>
                  <TableCell sx={headerCellSx}>Email</TableCell>
                  {isEnterprise && <TableCell sx={headerCellSx}>Org</TableCell>}
                  <TableCell sx={headerCellSx} align="right">AIC Consumed</TableCell>
                  <TableCell sx={headerCellSx} align="right">Spend</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRows
                  rows={modalRows}
                  startIndex={(modalPage - 1) * ROWS_PER_PAGE}
                  onRowClick={(row) => setSelectedUser(row)}
                />
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Divider />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 1.5,
              backgroundColor: "#fafafa",
              opacity: (isModalLoading || isModalFetching) ? 0.5 : 1,
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: "#777" }}>
              Showing {totalModalCount === 0 ? 0 : (modalPage - 1) * ROWS_PER_PAGE + 1}–
              {Math.min(modalPage * ROWS_PER_PAGE, totalModalCount)} of {totalModalCount} engineers
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton
                size="small"
                disabled={modalPage === 1 || isModalLoading || isModalFetching}
                onClick={() => setModalPage((p) => p - 1)}
                sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: "0.82rem", color: "#555", minWidth: 70, textAlign: "center" }}>
                Page {modalPage} of {totalModalPages}
              </Typography>
              <IconButton
                size="small"
                disabled={modalPage === totalModalPages || isModalLoading || isModalFetching}
                onClick={() => setModalPage((p) => p + 1)}
                sx={{ border: "1px solid #e8e8e8", borderRadius: "6px" }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* User detail slide-up modal — no enterprise-scoped day-by-day detail
          endpoint exists, so in enterprise mode this falls back to the
          selected row's own org_id (each user belongs to a specific org even
          when the list itself spans every org). */}
      <UserDetailModal
        user={selectedUser}
        open={Boolean(selectedUser)}
        onClose={() => setSelectedUser(null)}
        orgId={isEnterprise ? selectedUser?.org_id : orgId}
        costCenterId={isEnterprise ? undefined : costCenterId}
        platformId={platformId}
        fromDate={fromDate}
        toDate={toDate}
        deloitteOverageRate={deloitteOverageRate}
      />
    </>
  );
};

export default EngineerTable;
